/**
 * useTariffComparison Hook
 *
 * Fetches monthly fixed-vs-alternative tariff comparison via Postgres RPC.
 * Supports multiple tariff types (Agile, Trio) — normalises output columns
 * so the chart layer doesn't need to know which RPC was called.
 *
 * Normalised output per row:
 *   month, kwh_consumed, coverage_pct, days_in_window,
 *   fixed_total_excl_vat_pence, alt_total_excl_vat_pence,
 *   delta_excl_vat_pence, delta_pct
 */

import { useState, useEffect } from 'react'
import { supabase } from '@/supabase-client'

const DEFAULT_START = '2024-04-01'

const TARIFF_CONFIG = {
  agile: {
    rpc: 'calculate_agile_comparison',
    // Agile function returns agile_* columns — map to alt_*
    mapRow: (row) => ({
      ...row,
      alt_unit_excl_vat_pence: row.agile_unit_excl_vat_pence,
      alt_standing_excl_vat_pence: row.agile_standing_excl_vat_pence,
      alt_total_excl_vat_pence: row.agile_total_excl_vat_pence,
    }),
  },
  trio: {
    rpc: 'calculate_trio_comparison',
    // Trio function already returns alt_* columns
    mapRow: (row) => row,
  },
}

export const TARIFF_OPTIONS = [
  { value: 'agile', label: 'Shape Shifters: Agile' },
  { value: 'trio', label: 'Shape Shifters: Trio' },
]

export function useTariffComparison({ tariff = 'agile', start = DEFAULT_START, end } = {}) {
  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false

    async function load() {
      setIsLoading(true)
      setError(null)

      const config = TARIFF_CONFIG[tariff]
      if (!config) {
        setError(`Unknown tariff: ${tariff}`)
        setIsLoading(false)
        return
      }

      const resolvedEnd = end ?? new Date().toISOString().slice(0, 10)

      const { data: rows, error: rpcError } = await supabase.rpc(
        config.rpc,
        { p_start: start, p_end: resolvedEnd }
      )

      if (cancelled) return

      if (rpcError) {
        console.error(`[useTariffComparison] ${config.rpc} error:`, rpcError)
        setError(rpcError.message)
        setData([])
      } else {
        setData((rows ?? []).map(config.mapRow))
      }
      setIsLoading(false)
    }

    load()
    return () => { cancelled = true }
  }, [tariff, start, end])

  return { data, isLoading, error }
}

export default useTariffComparison
